/**
 * flexlayout-react layout presets — default, monitoring, trading.
 * Each preset is an IJsonModel describing the panel arrangement.
 */

import type { IJsonModel } from "flexlayout-react";

// ── Panel component keys (used in factory function) ─────────────────────

export const PANEL = {
  PNL_OVERVIEW: "pnl-overview",
  AGENT_POD: "agent-pod",
  ALPHA_ALERTS: "alpha-alerts",
  EVENT_STREAM: "event-stream",
  NEWS_FEED: "news-feed",
  POSITIONS: "positions",
  TRADE_HISTORY: "trade-history",
  RISK_DASHBOARD: "risk-dashboard",
  CONFLUENCE: "confluence",
  SYSTEM_HEALTH: "system-health",
} as const;

// Feature panels reusing the hardened components from src/features/* —
// kept OUT of PANEL on purpose: PANEL is the preset-shipped set (every id
// appears in the default preset, a tested invariant), while these are
// reachable only via the add-panel menu so existing saved layouts and
// presets stay untouched.
export const FEATURE_PANEL = {
  CROSS_MARKET: "cross-market",
  ORACLE: "oracle",
  RISK_CAPS: "risk-caps",
  SENTINEL: "sentinel",
  WHALE_FLOW: "whale-flow",
  SIGNAL_FEED: "signal-feed",
} as const;

export type PanelId =
  | (typeof PANEL)[keyof typeof PANEL]
  | (typeof FEATURE_PANEL)[keyof typeof FEATURE_PANEL];

/** Every registered panel id — drives the add-panel menu's missing list. */
export const ALL_PANEL_IDS: readonly PanelId[] = [
  ...Object.values(PANEL),
  ...Object.values(FEATURE_PANEL),
];

export const PANEL_TITLES: Record<PanelId, string> = {
  [PANEL.PNL_OVERVIEW]: "P&L Overview",
  [PANEL.AGENT_POD]: "Agent Pods",
  [PANEL.ALPHA_ALERTS]: "Alpha Alerts",
  [PANEL.EVENT_STREAM]: "Live Feed",
  [PANEL.NEWS_FEED]: "News",
  [PANEL.POSITIONS]: "Positions",
  [PANEL.TRADE_HISTORY]: "History",
  [PANEL.RISK_DASHBOARD]: "Risk",
  [PANEL.CONFLUENCE]: "Confluence",
  [PANEL.SYSTEM_HEALTH]: "System",
  // "Risk Caps" / "Signal Feed" deliberately differ from the existing
  // "Risk" (RISK_DASHBOARD) and "Live Feed" (EVENT_STREAM) titles —
  // distinct panels must never collide in the add-panel menu.
  [FEATURE_PANEL.CROSS_MARKET]: "Cross-Market",
  [FEATURE_PANEL.ORACLE]: "Oracle",
  [FEATURE_PANEL.RISK_CAPS]: "Risk Caps",
  [FEATURE_PANEL.SENTINEL]: "Sentinel",
  [FEATURE_PANEL.WHALE_FLOW]: "Whale Flow",
  [FEATURE_PANEL.SIGNAL_FEED]: "Signal Feed",
};

// ── Shared global config ────────────────────────────────────────────────

const GLOBAL = {
  splitterSize: 4,
  splitterExtra: 4,
  tabEnableClose: true,
  tabEnableDrag: true,
  tabEnableRename: false,
  tabSetEnableMaximize: true,
  tabSetEnableDrag: true,
  // flexlayout 0.10: tabSetEnableClose now controls tabset DELETION (false
  // would leave undeletable empty panes); the 0.8 intent — no close button
  // on tabsets — is tabSetEnableCloseButton.
  tabSetEnableCloseButton: false,
  tabSetMinWidth: 200,
  tabSetMinHeight: 100,
  borderMinSize: 100,
  borderBarSize: 30,
};

// ── Default Layout ──────────────────────────────────────────────────────
// P&L center, alerts + pods right, positions/history/events bottom

export const DEFAULT_LAYOUT: IJsonModel = {
  global: GLOBAL,
  borders: [
    {
      type: "border",
      location: "bottom",
      size: 28,
      children: [
        {
          type: "tab",
          name: "System",
          component: PANEL.SYSTEM_HEALTH,
          enableClose: false,
        },
      ],
    },
  ],
  layout: {
    type: "row",
    weight: 100,
    children: [
      // ── Main area (top) ───────────────────────────────────────
      {
        type: "row",
        weight: 65,
        children: [
          // Left: P&L chart
          {
            type: "tabset",
            weight: 60,
            children: [
              {
                type: "tab",
                name: "P&L Overview",
                component: PANEL.PNL_OVERVIEW,
                enableClose: false,
              },
            ],
          },
          // Right: Alerts + Agent Pods stacked
          {
            type: "row",
            weight: 40,
            children: [
              {
                type: "tabset",
                weight: 50,
                children: [
                  {
                    type: "tab",
                    name: "Alpha Alerts",
                    component: PANEL.ALPHA_ALERTS,
                  },
                  {
                    type: "tab",
                    name: "News",
                    component: PANEL.NEWS_FEED,
                  },
                ],
              },
              {
                type: "tabset",
                weight: 50,
                children: [
                  {
                    type: "tab",
                    name: "Agent Pods",
                    component: PANEL.AGENT_POD,
                  },
                  {
                    type: "tab",
                    name: "Risk",
                    component: PANEL.RISK_DASHBOARD,
                    // Money-relevant views must not be one accidental
                    // middle-click from gone (closure persists to storage).
                    enableClose: false,
                  },
                  {
                    type: "tab",
                    name: "Confluence",
                    component: PANEL.CONFLUENCE,
                  },
                ],
              },
            ],
          },
        ],
      },
      // ── Bottom dock: Positions / History / Events ─────────────
      {
        type: "tabset",
        weight: 35,
        children: [
          {
            type: "tab",
            name: "Positions",
            component: PANEL.POSITIONS,
            enableClose: false,
          },
          {
            type: "tab",
            name: "History",
            component: PANEL.TRADE_HISTORY,
          },
          {
            type: "tab",
            name: "Live Feed",
            component: PANEL.EVENT_STREAM,
          },
        ],
      },
    ],
  },
};

// ── Monitoring Layout ───────────────────────────────────────────────────
// Event stream center (large), all status panels visible

export const MONITORING_LAYOUT: IJsonModel = {
  global: GLOBAL,
  borders: [],
  layout: {
    type: "row",
    weight: 100,
    children: [
      {
        type: "row",
        weight: 70,
        children: [
          {
            type: "tabset",
            weight: 60,
            children: [
              {
                type: "tab",
                name: "Live Feed",
                component: PANEL.EVENT_STREAM,
                enableClose: false,
              },
              {
                type: "tab",
                name: "News",
                component: PANEL.NEWS_FEED,
              },
            ],
          },
          {
            type: "row",
            weight: 40,
            children: [
              {
                type: "tabset",
                weight: 50,
                children: [
                  {
                    type: "tab",
                    name: "Agent Pods",
                    component: PANEL.AGENT_POD,
                  },
                  {
                    type: "tab",
                    name: "System",
                    component: PANEL.SYSTEM_HEALTH,
                  },
                ],
              },
              {
                type: "tabset",
                weight: 50,
                children: [
                  {
                    type: "tab",
                    name: "Risk",
                    component: PANEL.RISK_DASHBOARD,
                    enableClose: false,
                  },
                  {
                    type: "tab",
                    name: "Confluence",
                    component: PANEL.CONFLUENCE,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        type: "tabset",
        weight: 30,
        children: [
          {
            type: "tab",
            name: "Positions",
            component: PANEL.POSITIONS,
            enableClose: false,
          },
          {
            type: "tab",
            name: "P&L Overview",
            component: PANEL.PNL_OVERVIEW,
          },
        ],
      },
    ],
  },
};

// ── Trading Layout ──────────────────────────────────────────────────────
// Alpha alerts center (large), positions bottom (large), P&L sidebar

export const TRADING_LAYOUT: IJsonModel = {
  global: GLOBAL,
  borders: [],
  layout: {
    type: "row",
    weight: 100,
    children: [
      {
        type: "row",
        weight: 60,
        children: [
          {
            type: "tabset",
            weight: 65,
            children: [
              {
                type: "tab",
                name: "Alpha Alerts",
                component: PANEL.ALPHA_ALERTS,
                enableClose: false,
              },
              {
                type: "tab",
                name: "News",
                component: PANEL.NEWS_FEED,
              },
            ],
          },
          {
            type: "tabset",
            weight: 35,
            children: [
              {
                type: "tab",
                name: "P&L Overview",
                component: PANEL.PNL_OVERVIEW,
              },
              {
                type: "tab",
                name: "Agent Pods",
                component: PANEL.AGENT_POD,
              },
            ],
          },
        ],
      },
      {
        type: "tabset",
        weight: 40,
        children: [
          {
            type: "tab",
            name: "Positions",
            component: PANEL.POSITIONS,
            enableClose: false,
          },
          {
            type: "tab",
            name: "History",
            component: PANEL.TRADE_HISTORY,
          },
          {
            type: "tab",
            name: "Live Feed",
            component: PANEL.EVENT_STREAM,
          },
          {
            type: "tab",
            name: "Risk",
            component: PANEL.RISK_DASHBOARD,
            enableClose: false,
          },
        ],
      },
    ],
  },
};

// ── Preset Map ──────────────────────────────────────────────────────────

export const LAYOUT_PRESETS: Record<string, IJsonModel> = {
  default: DEFAULT_LAYOUT,
  monitoring: MONITORING_LAYOUT,
  trading: TRADING_LAYOUT,
};

export function getPreset(name: string): IJsonModel {
  return LAYOUT_PRESETS[name] ?? DEFAULT_LAYOUT;
}
